import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Phone } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Reveal } from "./Reveal";
import { PHONE_DISPLAY, PHONE_TEL } from "./Header";

const API = "";

const fieldClass =
  "bg-transparent border-0 border-b border-white/20 rounded-none px-0 h-12 text-slate-100 placeholder:text-slate-500 focus-visible:ring-0 focus-visible:border-teal-400 transition-colors duration-300";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", company: "", message: "" });
  const [sending, setSending] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
  await axios.post(`${API}/enquiry.php`, {
        name: form.name,
        email: form.email,
        company: form.company || undefined,
        message: form.message,
      });
      toast.success("Enquiry sent — James will be in touch shortly.");
      setForm({ name: "", email: "", company: "", message: "" });
    } catch (err) {
      toast.error(`Something went wrong — please call James directly on ${PHONE_DISPLAY}.`);
    } finally {
      setSending(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="py-24 md:py-32 bg-[#07131F] border-t border-white/5"
    >
      <div className="mx-auto max-w-7xl px-6 lg:px-10 grid grid-cols-12 gap-12 md:gap-16">
        <div className="col-span-12 md:col-span-6">
          <Reveal>
            <p className="text-xs md:text-sm tracking-[0.25em] uppercase text-teal-400 font-medium">
              Contact
            </p>
            <h2 className="mt-4 font-display text-3xl md:text-5xl tracking-tight leading-tight text-slate-50">
              Start the conversation.
            </h2>
            <p className="mt-6 max-w-md text-base md:text-lg leading-relaxed text-slate-300">
              The fastest way to scope a problem is a short call. If you'd prefer to write first,
              send an enquiry and James will come back to you.
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <a
              href={PHONE_TEL}
              data-testid="contact-call-james-btn"
              className="group mt-10 inline-flex items-center gap-4"
            >
              <span className="flex h-14 w-14 items-center justify-center rounded-full bg-teal-400 text-[#02060D] group-hover:bg-teal-300 group-hover:shadow-[0_0_32px_rgba(45,212,191,0.45)] transition-[background-color,box-shadow] duration-300">
                <Phone className="h-5 w-5" />
              </span>
              <span>
                <span className="block text-xs tracking-[0.2em] uppercase text-slate-400">
                  Call James directly
                </span>
                <span className="block font-display text-2xl md:text-3xl text-slate-50 group-hover:text-teal-300 transition-colors duration-300">
                  {PHONE_DISPLAY}
                </span>
              </span>
            </a>
          </Reveal>
        </div>

        <Reveal className="col-span-12 md:col-span-6" delay={0.12}>
          <form
            onSubmit={onSubmit}
            data-testid="enquiry-form"
            className="bg-[#02060D] border border-white/10 p-8 md:p-10"
          >
            <div className="grid sm:grid-cols-2 gap-x-6 gap-y-7">
              <div>
                <label htmlFor="enquiry-name" className="text-xs tracking-[0.2em] uppercase text-slate-400">
                  Name *
                </label>
                <Input
                  id="enquiry-name"
                  data-testid="enquiry-name-input"
                  aria-label="Your name"
                  required
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Your name"
                  className={fieldClass}
                />
              </div>
              <div>
                <label htmlFor="enquiry-email" className="text-xs tracking-[0.2em] uppercase text-slate-400">
                  Email *
                </label>
                <Input
                  id="enquiry-email"
                  type="email"
                  data-testid="enquiry-email-input"
                  aria-label="Your email"
                  required
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="you@company.com"
                  className={fieldClass}
                />
              </div>
              <div className="sm:col-span-2">
                <label htmlFor="enquiry-company" className="text-xs tracking-[0.2em] uppercase text-slate-400">
                  Company
                </label>
                <Input
                  id="enquiry-company"
                  data-testid="enquiry-company-input"
                  aria-label="Your company"
                  value={form.company}
                  onChange={(e) => setForm({ ...form, company: e.target.value })}
                  placeholder="Organisation (optional)"
                  className={fieldClass}
                />
              </div>
              <div className="sm:col-span-2">
                <label htmlFor="enquiry-message" className="text-xs tracking-[0.2em] uppercase text-slate-400">
                  The problem you're trying to solve *
                </label>
                <Textarea
                  id="enquiry-message"
                  data-testid="enquiry-message-input"
                  aria-label="Your message"
                  required
                  rows={4}
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  placeholder="A sentence or two is plenty — we'll scope the rest on a call."
                  className={`${fieldClass} h-auto resize-none`}
                />
              </div>
            </div>
            <button
              type="submit"
              disabled={sending}
              data-testid="enquiry-submit-button"
              className="group mt-9 inline-flex w-full items-center justify-center gap-2.5 rounded-full bg-teal-400 px-7 py-3.5 text-sm font-semibold text-[#02060D] hover:bg-teal-300 hover:shadow-[0_0_28px_rgba(45,212,191,0.35)] disabled:opacity-60 transition-[background-color,box-shadow,opacity] duration-300"
            >
              {sending ? "Sending…" : "Send enquiry"}
              <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform duration-300" />
            </button>
            <p
              data-testid="enquiry-privacy-note"
              className="mt-4 text-center text-xs leading-relaxed text-slate-500"
            >
              Your details are used only to respond to your enquiry — never shared, sold or used
              for marketing lists.
            </p>
          </form>
        </Reveal>
      </div>
    </section>
  );
}
